/**
 * Componente ReusedAgGridTable
 * 
 * Wrapper sobre AgGridTable para la lista de Recepciones de Rehusados.
 * Maneja la configuración específica de columnas, sorting y acciones.
 */

import React, { useCallback, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { ReturnedReception } from '../../models';

interface ReusedAgGridTableProps {
    reused: ReturnedReception[];
    isLoading?: boolean;
    isFetching?: boolean;
    className?: string;
    selectedColumns?: string[];
    fetchNextPage?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    sorting?: { field: string; direction: 'asc' | 'desc' } | null;
    onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
}

export const ReusedAgGridTable = React.memo(function ReusedAgGridTable({
    reused,
    isFetching = false,
    selectedColumns = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sorting,
    onSortingChange,
}: ReusedAgGridTableProps) {
    const agGridRef = useRef<AgGridTableRef>(null);

    // Usar el hook para manejar las columnas
    const { filteredColumnDefs } = useAgGridColumns({
        selectedColumns,
    });

    // Handler no-op para onGridReady
    const handleGridReady = useCallback(() => { }, []);

    // Las columnas ya vienen con sus cellRenderers desde el hook
    const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

    return (
        <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Componente base AgGridTable */}
            <AgGridTable
                ref={agGridRef}
                data={reused}
                columnDefs={columnDefsWithRenderers}
                height="calc(100vh - 150px)"
                enableSelection={false}
                onGridReady={handleGridReady}
                className="rounded-lg shadow-sm"
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                gridOptions={{}}
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        </div>
    );
});

